import { useState } from 'react';
import type { RequestGroup, TrackedRequest } from '../../types';
import { Button, Card, Notice } from '../ui/primitives';
import { AudiencePushForm } from './AudiencePushForm';
import { Pill, agencyLabel, formatDate, formatDateTime, pushProgress } from './shared';
import {
  CopyIcon,
  ExternalIcon,
  EyeIcon,
  IconButton,
  IconLink,
  PencilIcon,
} from './icons';

/**
 * The tabs of one brand-and-agency combination.
 *
 * A rollout document gains a tab per push request, and this is where they are
 * managed: one row per tab, with the audience push recorded against the tab it
 * belongs to. The list before it shows one row per combination, so this screen is
 * where "which tab?" is answered — a question the flat list could only answer by
 * making the operator read eleven near-identical rows.
 */
export function CombinationTabs({
  group,
  onBack,
  onRequestUpdated,
  onOpenForm,
}: {
  group: RequestGroup;
  onBack: () => void;
  /** Lifted so the list behind this screen stays in step. */
  onRequestUpdated: (updated: TrackedRequest) => void;
  /**
   * Opens the rollout form for one tab.
   *
   * The flow controller owns the form, because amending and duplicating both end
   * in screens it already drives — the destination and processing steps.
   */
  onOpenForm: (entry: TrackedRequest, mode: 'view' | 'amend' | 'duplicate') => void;
}) {
  const [recording, setRecording] = useState<TrackedRequest | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const progress = pushProgress(group.pushedCount, group.cohortCount);

  return (
    <div className="space-y-5">
      <Card className="p-4 sm:p-6">
        <Button variant="ghost" onClick={onBack} className="mb-3 px-0">
          ← All requests
        </Button>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <p className="text-section font-semibold text-ink">{group.brandName || '—'}</p>
            <p className="mt-0.5 text-field text-ink-muted">
              {agencyLabel({
                businessType: group.businessType,
                agencyName: group.agencyName,
                subAgencyName: '',
              })}
              {group.subAgencyNames.length ? (
                <span className="text-ink-faint"> · {group.subAgencyNames.join(', ')}</span>
              ) : null}
            </p>
            <p className="mt-1 truncate text-micro text-ink-faint" title={group.documentName}>
              {group.documentName}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Pill tone={progress.tone}>{progress.text}</Pill>
            {group.pushCount > group.pushedCount ? (
              <Pill tone="none">{group.pushCount} pushes</Pill>
            ) : null}
            <Pill tone="none">
              {group.tabCount} tab{group.tabCount === 1 ? '' : 's'}
            </Pill>
            <IconLink label="Open the document in Google Docs" href={group.documentUrl}>
              <ExternalIcon />
            </IconLink>
          </div>
        </div>
      </Card>

      {note ? (
        <Notice
          tone="info"
          title="Audience push recorded"
          action={
            <Button variant="ghost" onClick={() => setNote(null)}>
              Dismiss
            </Button>
          }
        >
          {note}
        </Notice>
      ) : null}

      {/*
        The same reasoning as the list before it: five columns plus five actions
        cannot fit a phone, so below `md` each tab becomes a card with its actions
        along the bottom, where they have room to be tapped.
      */}
      <Card className="overflow-hidden md:hidden">
        <ul className="divide-y divide-line">
          {group.requests.map((entry) => {
            const tabProgress = pushProgress(entry.pushedCount, entry.cohortCount);

            return (
              <li key={entry.id} className="px-4 py-4">
                <p className="text-field font-medium text-ink">{entry.tabTitle}</p>
                <p className="mt-0.5 text-micro text-ink-faint">
                  {entry.channel} · {entry.action === 'ADD_TAB' ? 'added to document' : 'created document'}
                </p>

                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  <Pill tone={tabProgress.tone}>{tabProgress.text}</Pill>
                  {entry.pushCount > entry.pushedCount ? (
                    <span className="text-micro text-ink-muted">{entry.pushCount} pushes</span>
                  ) : null}
                </div>

                <p className="mt-1.5 text-micro text-ink-faint">
                  {formatDateTime(entry.createdAt)}
                  {entry.pushRecordedAt ? ` · updated ${formatDate(entry.pushRecordedAt)}` : ''}
                </p>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button
                    variant="secondary"
                    className="px-3 py-1.5"
                    onClick={() => setRecording(entry)}
                  >
                    Record push
                  </Button>
                  <IconButton label="View details" onClick={() => onOpenForm(entry, 'view')}>
                    <EyeIcon />
                  </IconButton>
                  <IconButton
                    label="Edit and update the document"
                    onClick={() => onOpenForm(entry, 'amend')}
                  >
                    <PencilIcon />
                  </IconButton>
                  <IconButton
                    label="Duplicate into a new tab"
                    onClick={() => onOpenForm(entry, 'duplicate')}
                  >
                    <CopyIcon />
                  </IconButton>
                  <IconLink label="Open this tab in Google Docs" href={entry.tabUrl}>
                    <ExternalIcon />
                  </IconLink>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>

      <Card className="hidden overflow-hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[56rem] table-fixed border-collapse text-left">
            <colgroup>
              <col className="w-[22%]" />
              <col className="w-[12%]" />
              <col className="w-[20%]" />
              <col className="w-[16%]" />
              <col className="w-[30%]" />
            </colgroup>
            <thead>
              <tr className="border-b border-line bg-surface-muted">
                {['Tab', 'Channel', 'Audience push', 'Submitted', ''].map((heading, index) => (
                  <th
                    key={heading || `actions-${index}`}
                    scope="col"
                    className="px-4 py-3 text-micro font-semibold uppercase tracking-wide text-ink-muted"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {group.requests.map((entry) => {
                const tabProgress = pushProgress(entry.pushedCount, entry.cohortCount);

                return (
                  <tr key={entry.id} className="border-b border-line align-top last:border-0">
                    <td className="px-4 py-4">
                      <span className="block truncate text-field font-medium text-ink">
                        {entry.tabTitle}
                      </span>
                      <span className="mt-0.5 block text-micro text-ink-faint">
                        {entry.action === 'ADD_TAB' ? 'Added to document' : 'Created document'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-field text-ink-muted">{entry.channel}</td>
                    <td className="px-4 py-4">
                      <Pill tone={tabProgress.tone}>{tabProgress.text}</Pill>
                      {/*
                        A cohort can be pushed more than once, so the number of
                        pushes is worth saying when it differs from the number of
                        cohorts covered — otherwise "All 1 pushed" hides that two
                        audiences went live.
                      */}
                      {entry.pushCount > entry.pushedCount ? (
                        <span className="mt-1 block text-micro text-ink-muted">
                          {entry.pushCount} pushes recorded
                        </span>
                      ) : null}
                      {entry.pushRecordedAt ? (
                        <span className="mt-1 block text-micro text-ink-faint">
                          Updated {formatDate(entry.pushRecordedAt)}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-4">
                      <span className="block text-field text-ink-muted">
                        {formatDateTime(entry.createdAt) || '—'}
                      </span>
                      <span
                        className="mt-0.5 block truncate text-micro text-ink-faint"
                        title={entry.submittedBy}
                      >
                        {entry.submittedBy || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      {/*
                        Recording a push is why an operator opens this screen, so
                        it keeps its words. The rest are icons — five text buttons
                        did not fit, and shrinking them only made them unreadable.
                      */}
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="secondary"
                          className="mr-1 px-3 py-1.5"
                          onClick={() => setRecording(entry)}
                        >
                          Record push
                        </Button>

                        <IconButton label="View details" onClick={() => onOpenForm(entry, 'view')}>
                          <EyeIcon />
                        </IconButton>

                        <IconButton
                          label="Edit and update the document"
                          onClick={() => onOpenForm(entry, 'amend')}
                        >
                          <PencilIcon />
                        </IconButton>

                        <IconButton
                          label="Duplicate into a new tab"
                          onClick={() => onOpenForm(entry, 'duplicate')}
                        >
                          <CopyIcon />
                        </IconButton>

                        {/*
                          The tab-scoped link, not the plain document one: a
                          document link opens whichever tab Docs last showed.
                        */}
                        <IconLink label="Open this tab in Google Docs" href={entry.tabUrl}>
                          <ExternalIcon />
                        </IconLink>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {recording ? (
        <AudiencePushForm
          entry={recording}
          onClose={() => setRecording(null)}
          onSaved={(updated, saveNote) => {
            onRequestUpdated(updated);
            setNote(saveNote);
            setRecording(null);
          }}
        />
      ) : null}
    </div>
  );
}
